import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Menu,
  FormControlLabel,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import DraggableDialog from '../components/common/DraggableDialog';
import UserAccessDialog from '../components/userManagement/UserAccessDialog';
import { useDispatch } from 'react-redux';
import apiService from '../services/api.service';
import { showSnackbar, setLoading } from '../store/slices/uiSlice';

interface User {
  id: number;
  username: string;
  email: string;
  fullname: string;
  department?: string;
  /**
   * Job title shown on the Payment Certificate next to the Initiator's
   * name (e.g. "Procurement Officer", "Project Manager"). Edit via
   * the Edit User dialog. Falls back to department/role on the PDF
   * when null/empty.
   */
  designation?: string;
  role: string;
  sapusercode?: string;
  /** DocuSign User API ID (UUID) used for Tier B impersonation. */
  docusign_user_api_id?: string | null;
  isactive: boolean;
  createdat?: string;
  /** Users.AllCompanies: every active company, including companies added later. */
  allcompanies?: boolean;
  /** The companies the user may open (UserCompanyAccess), for the company filter. */
  companyids?: number[];
  /** Per company where something is granted: All, or how many and the first codes, of each kind. */
  accesssummary?: AccessSummary[];
  /** Notes the move to per-company access left for an administrator to check. */
  accessreviewcount?: number;
}

type KindSummary = { all: boolean; count: number; codes: string[] };
type AccessSummary = { companyid: number; companyname: string; projects: KindSummary; divisions: KindSummary; departments: KindSummary };

const kindSummary = (row: any, kind: 'projects' | 'divisions' | 'departments'): KindSummary => ({
  all: row?.[`${kind}all`] === true,
  count: Number(row?.[`${kind}count`]) || 0,
  codes: Array.isArray(row?.[`${kind}codes`]) ? row[`${kind}codes`].map(String) : [],
});

/** "All projects · Div. DCP00 · Dept. OPE01, FAD01": projects as a count, divisions and departments by code up to 3. */
const describeAccess = (s: AccessSummary): string => {
  const part = (k: KindSummary, label: string, one: string, many: string, all: string) => {
    if (k.all) return all;
    if (!k.count) return '';
    if (label && k.count <= 3 && k.codes.length === k.count) return `${label} ${k.codes.join(', ')}`;
    return `${k.count} ${k.count === 1 ? one : many}`;
  };
  return [
    part(s.projects, '', 'project', 'projects', 'All projects'),
    part(s.divisions, 'Div.', 'division', 'divisions', 'All divisions'),
    part(s.departments, 'Dept.', 'department', 'departments', 'All departments'),
  ].filter(Boolean).join(' · ') || 'nothing granted';
};

// Fallback list used while roles are loading or if the lookup fails. The live
// list is fetched from /api/user-management/users/roles/available.
const FALLBACK_ROLE_OPTIONS = [
  'Initiator',
  'ProjectManager',
  'CostCenterManager',
  'OperationsManager',
  'FinanceManager',
  'GeneralManager',
  'ProcurementOfficer',
  'Admin',
  'Viewer',
  'ProjectCostControl',
  'ProjectControlsDirector',
];

type EditDialogState = {
  open: boolean;
  user?: User;
  mode: 'create' | 'edit' | 'resetPassword';
};

export default function UserManagement() {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [roleOptions, setRoleOptions] = useState<string[]>(FALLBACK_ROLE_OPTIONS);
  const [filters, setFilters] = useState<{ role?: string; active?: boolean }>({ active: true });
  const [editState, setEditState] = useState<EditDialogState>({ open: false, mode: 'edit' });
  const [form, setForm] = useState<Partial<User> & { password?: string }>({});
  // Roles held on top of the main role (UserRoles), e.g. the item request approval roles.
  const [additionalRoles, setAdditionalRoles] = useState<string[]>([]);
  const [loading, setLocalLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [rowMenuUser, setRowMenuUser] = useState<User | null>(null);
  const rowMenuOpen = Boolean(rowMenuAnchorEl);
  // Company linking state
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogUser, setCompanyDialogUser] = useState<User | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Array<{ companyid: number; companycode: string; companyname: string; databasename: string }>>([]);
  const [userCompanies, setUserCompanies] = useState<Array<{ companyid: number; companycode: string; companyname: string; databasename: string; isprimary?: boolean }>>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  // "All companies": every active company, including companies added later (Users.AllCompanies).
  const [userAllCompanies, setUserAllCompanies] = useState(false);
  // Access: projects, divisions and departments per company (components/userManagement/UserAccessDialog).
  const [accessDialogUser, setAccessDialogUser] = useState<User | null>(null);
  const accessChanged = useRef(false);
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [reviewOnly, setReviewOnly] = useState(false);
  // Module access state
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleDialogUser, setModuleDialogUser] = useState<User | null>(null);
  const [userModules, setUserModules] = useState<string[]>([]);
  const normalizeModuleKeys = (modules: any[]): string[] =>
    Array.from(
      new Set(
        modules
          .map((m: any) => String((m?.modulekey ?? m?.ModuleKey ?? m) || '').trim())
          .filter(Boolean)
      )
    );
  const allModules = useMemo(() => ([
    { key: 'dashboard', label: 'Dashboard', category: 'Core' },
    { key: 'pr-dashboard', label: 'PR Dashboard', category: 'PR / PO' },
    { key: 'dashboard-pr-kpis', label: 'Dashboard PR KPIs', category: 'Core' },
    { key: 'dashboard-po-kpis', label: 'Dashboard PO KPIs', category: 'Core' },
    { key: 'dashboard-subcontract-kpis', label: 'Dashboard Subcontract KPIs', category: 'Core' },
    { key: 'dashboard-payment-request-kpis', label: 'Dashboard Payment Request KPIs', category: 'Core' },
    { key: 'docusign-signing', label: 'DocuSign Signing', category: 'Core' },
    { key: 'create-pr', label: 'Create PR', category: 'PR / PO' },
    { key: 'create-po', label: 'Create PO', category: 'PR / PO' },
    { key: 'pr-inquiry', label: 'PR Inquiry', category: 'PR / PO' },
    { key: 'po-inquiry', label: 'PO Inquiry', category: 'PR / PO' },
    { key: 'grpo', label: 'GRPO (Goods Receipt PO)', category: 'PR / PO' },
    { key: 'post-sap', label: 'Post To SAP', category: 'PR / PO' },
    { key: 'sap-posting-history', label: 'SAP Posting History', category: 'PR / PO' },
    { key: 'create-payment-request', label: 'Payment Requests', category: 'PR / PO' },
    { key: 'auto-invoice', label: 'Auto Invoice', category: 'PR / PO' },
    { key: 'sales-stock', label: 'Live Stock Check', category: 'Sales Portal' },
    { key: 'sales-order-create', label: 'Create Sales Order', category: 'Sales Portal' },
    { key: 'sales-orders', label: 'Sales Orders', category: 'Sales Portal' },
    { key: 'sales-soa', label: 'Customer SOA', category: 'Sales Portal' },
    { key: 'formatted-soa', label: 'Formatted SOA (print statements)', category: 'Sales Portal' },
    { key: 'formatted-soa-designer', label: 'Statement Templates (designer)', category: 'Sales Portal' },
    { key: 'sales-invoice-print', label: 'Print A/R Invoices', category: 'Sales Portal' },
    { key: 'sales-order-print', label: 'Print Sales Orders', category: 'Sales Portal' },
    { key: 'sales-approvals', label: 'Sales Order Approvals', category: 'Sales Portal' },
    { key: 'sales-order-report', label: 'Sales Order Report', category: 'Sales Portal' },
    { key: 'sales-portal-settings', label: 'Sales Portal Settings', category: 'Sales Portal' },
    { key: 'warehouse-receiving', label: 'Warehouse Receiving (Scan)', category: 'Warehouse' },
    { key: 'warehouse-barcode', label: 'Barcode (Warehouse)', category: 'Warehouse' },
    { key: 'warehouse-picking-list', label: 'Picking List (Warehouse)', category: 'Warehouse' },
    { key: 'warehouse-item-seed', label: 'Item Seed (Warehouse)', category: 'Warehouse' },
    { key: 'item-requests', label: 'Create New Item / Item Requests', category: 'Warehouse' },
    { key: 'mcsr-report', label: 'MCSR Report', category: 'MCSR' },
    { key: 'mcsr-dashboard', label: 'MCSR Dashboard', category: 'MCSR' },
    { key: 'mcsr-sync', label: 'MCSR Sync Monitor', category: 'MCSR' },
    { key: 'project-reports', label: 'Project Monthly Reports', category: 'Project Reports' },
    { key: 'project-issues', label: 'Project Issues (management)', category: 'Project Reports' },
    { key: 'petty-cash', label: 'Petty Cash Claims', category: 'Petty Cash' },
    { key: 'petty-cash-setup', label: 'Petty Cash Setup (holders, categories)', category: 'Petty Cash' },
    { key: 'suppliers', label: 'Suppliers (create SAP vendors)', category: 'Petty Cash' },
    { key: 'e2i-enquiries', label: 'Enquiries', category: 'Enquiry to Invoice' },
    { key: 'e2i-invoice-requests', label: 'Client Invoice Requests', category: 'Enquiry to Invoice' },
    { key: 'e2i-register', label: 'Quotation Register', category: 'Enquiry to Invoice' },
    { key: 'e2i-setup', label: 'Enquiry to Invoice Setup', category: 'Enquiry to Invoice' },
    { key: 'presales', label: 'Presales AI Agent', category: 'Presales' },
    { key: 'customer-contacts', label: 'Customer Contacts', category: 'Customer Contacts' },
    { key: 'inbox', label: 'Inbox', category: 'Workflow' },
    { key: 'in-progress', label: 'In Progress', category: 'Workflow' },
    { key: 'completed', label: 'Completed', category: 'Workflow' },
    { key: 'budget', label: 'Budget', category: 'Reporting' },
    { key: 'reports', label: 'Reports', category: 'Reporting' },
    { key: 'pr-detail-report', label: 'PR Detail Report', category: 'Reporting' },
    { key: 'create-subcontract', label: 'Create Subcontract', category: 'Subcontract' },
    { key: 'subcontract-documents', label: 'Subcontract Documents', category: 'Subcontract' },
    { key: 'payment-certificate', label: 'Payment Certificate', category: 'Subcontract' },
    { key: 'subcontract-variation', label: 'Subcontract Variation', category: 'Subcontract' },
    { key: 'subcontract-work-done', label: 'Subcontract Work Done', category: 'Subcontract' },
    { key: 'users', label: 'User Management', category: 'Settings' },
    { key: 'companies', label: 'Company Management', category: 'Settings' },
    { key: 'general-settings', label: 'General Settings', category: 'Settings' },
    { key: 'approval-flow-settings', label: 'Approval Flow', category: 'Settings' },
    { key: 'sap-approval-workflows', label: 'SAP Approval Workflows', category: 'Settings' },
    { key: 'accounts-notifications', label: 'Accounts notifications', category: 'Settings' },
    { key: 'payment-request-workflow', label: 'Payment Request Workflow', category: 'Settings' },
    { key: 'bulk-workflow-management', label: 'Bulk Workflow Management', category: 'Settings' },
    { key: 'gl-rule-year-copy', label: 'G/L Rules Year Copy', category: 'Settings' },
    { key: 'projects', label: 'Project Management', category: 'Settings' },
    { key: 'role-management', label: 'Role Management', category: 'Settings' },
    { key: 'cost-center-mapping', label: 'Cost Center Mapping', category: 'Settings' },
    { key: 'transaction-sync', label: 'Transaction Sync', category: 'Settings' },
    { key: 'system-monitor', label: 'System Monitor', category: 'Settings' },
    { key: 'schedule-jobs', label: 'Schedule Jobs', category: 'Settings' },
  ].sort((a, b) => a.label.localeCompare(b.label))), []);
  const moduleGroups = useMemo(() => {
    const grouped = allModules.reduce((acc, module) => {
      (acc[module.category] = acc[module.category] || []).push(module);
      return acc;
    }, {} as Record<string, typeof allModules>);
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [allModules]);
  const visibleModuleKeys = useMemo(() => new Set(allModules.map((module) => module.key)), [allModules]);
  const userModuleSet = useMemo(() => new Set(userModules), [userModules]);
  const visibleAssignedCount = useMemo(
    () => allModules.filter((module) => userModuleSet.has(module.key)).length,
    [allModules, userModuleSet]
  );
  const hiddenAssignedModules = useMemo(
    () => userModules.filter((moduleKey) => !visibleModuleKeys.has(moduleKey)),
    [userModules, visibleModuleKeys]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchUsers = async () => {
    try {
      setLocalLoading(true);
      dispatch(setLoading(true));
      const res = await apiService.getUsers(filters);
      const raw = Array.isArray(res) ? res : res?.data || [];
      const list: User[] = (raw as any[]).map((u: any) => ({
        id: u.UserID ?? u.userid ?? u.id,
        username: u.Username ?? u.username,
        email: u.Email ?? u.email,
        fullname: u.FullName ?? u.fullname,
        department: u.Department ?? u.department ?? '',
        designation: u.Designation ?? u.designation ?? '',
        role: u.Role ?? u.role,
        sapusercode: u.SAPUserCode ?? u.sapusercode ?? '',
        docusign_user_api_id: u.docusign_user_api_id ?? u.docusignUserApiId ?? '',
        isactive: (u.IsActive ?? u.isactive) === true,
        createdat: u.CreatedDate ?? u.createdat,
        allcompanies: u.allcompanies === true,
        companyids: Array.isArray(u.companyids) ? u.companyids.map(Number) : [],
        accesssummary: (Array.isArray(u.accesssummary) ? u.accesssummary : []).map((s: any) => ({
          companyid: Number(s.companyid),
          companyname: String(s.companyname || ''),
          projects: kindSummary(s, 'projects'),
          divisions: kindSummary(s, 'divisions'),
          departments: kindSummary(s, 'departments'),
        })),
        accessreviewcount: Number(u.accessreviewcount) || 0,
      }));
      setUsers(list);
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Failed to load users', severity: 'error' }));
    } finally {
      setLocalLoading(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.role, filters.active]);

  // The active companies, for the company filter (Manage Companies reloads them when it opens).
  useEffect(() => {
    apiService.getCompanies(true)
      .then((res: any) => {
        const raw = Array.isArray(res) ? res : res?.data || [];
        setAvailableCompanies(raw.map(toCompany));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the active role catalog from the backend so the dropdown stays in
  // sync with the Roles table (Role Management). Falls back to the static
  // list if the lookup fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getAvailableUserRoles();
        if (cancelled) return;
        const names: string[] = Array.isArray(res?.data)
          ? res.data.map((r: any) => r.rolename).filter(Boolean)
          : [];
        if (names.length > 0) {
          // Merge with fallback so we always include any well-known roles.
          const merged = Array.from(new Set([...names, ...FALLBACK_ROLE_OPTIONS]));
          setRoleOptions(merged);
        }
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCreate = () => {
    setForm({ username: '', email: '', fullname: '', department: '', designation: '', role: 'Viewer', sapusercode: '' });
    setEditState({ open: true, mode: 'create' });
  };

  const openEdit = (user: User) => {
    setForm({ ...user });
    setAdditionalRoles([]);
    setEditState({ open: true, mode: 'edit', user });
    apiService.getUserAdditionalRoles(Number((user as any).id ?? (user as any).userid))
      .then((res: any) => setAdditionalRoles(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setAdditionalRoles([]));
  };

  // A company is shown with its SAP database, as in the company picker at the top.
  const toCompany = (c: any) => ({
    companyid: c.CompanyID ?? c.companyid,
    companycode: c.CompanyCode ?? c.companycode,
    companyname: c.CompanyName ?? c.companyname,
    databasename: c.DatabaseName ?? c.databasename ?? '',
  });
  const loadUserCompanies = async (userId: number) => {
    const ucRes = await apiService.getUserCompanies(userId);
    const ucRaw = Array.isArray(ucRes) ? ucRes : ucRes?.data || [];
    setUserCompanies(ucRaw.map((uc: any) => ({ ...toCompany(uc), isprimary: uc.IsPrimary ?? uc.isprimary })));
    setUserAllCompanies((ucRes as any)?.allCompanies === true);
  };

  const openCompanyDialog = async (user: User) => {
    try {
      setCompanyDialogUser(user);
      setCompanyDialogOpen(true);
      // Load available active companies
      const compRes = await apiService.getCompanies(true);
      const raw = Array.isArray(compRes) ? compRes : compRes?.data || [];
      setAvailableCompanies(raw.map(toCompany));
      await loadUserCompanies(user.id);
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Failed to load companies', severity: 'error' }));
    }
  };

  const openAccessDialog = (user: User) => {
    accessChanged.current = false;
    setAccessDialogUser(user);
  };

  const closeAccessDialog = () => {
    setAccessDialogUser(null);
    if (accessChanged.current) fetchUsers();
  };

  const openResetPassword = (user: User) => {
    setForm({ id: user.id, password: '' });
    setEditState({ open: true, mode: 'resetPassword', user });
  };

  const openModuleDialog = async (user: User) => {
    try {
      setModuleDialogUser(user);
      setModuleDialogOpen(true);
      const res = await apiService.getUserModules(user.id);
      const raw = Array.isArray(res) ? res : res?.data || [];
      setUserModules(normalizeModuleKeys(raw));
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Failed to load module access', severity: 'error' }));
    }
  };

  const closeDialog = () => {
    setEditState({ open: false, mode: 'edit', user: undefined });
    setForm({});
  };

  const handleSave = async () => {
    try {
      dispatch(setLoading(true));
      if (editState.mode === 'create') {
        const username = String(form.username || '').trim();
        const email = String(form.email || '').trim();
        const fullname = String(form.fullname || '').trim();
        const role = String(form.role || 'Viewer');
        const password = String((form.password || 'Welcome@123')).trim();
        const errors: string[] = [];
        if (username.length < 3 || username.length > 100) errors.push('Username must be between 3 and 100 characters');
        const emailOk = /.+@.+\..+/.test(email);
        if (!emailOk) errors.push('Invalid email address');
        if (fullname.length < 2 || fullname.length > 255) errors.push('Full name must be between 2 and 255 characters');
        if (password.length < 8) errors.push('Password must be at least 8 characters');
        const pwComplex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password);
        if (!pwComplex) errors.push('Password must include uppercase, lowercase, number, special character');
        // Validate the role against the live catalog the dropdown is populated
        // from (loaded from /api/user-management/users/roles/available + the
        // FALLBACK_ROLE_OPTIONS list). This automatically accepts any role
        // added via Role Management (APTeam, ChiefAccountant, CEO, custom roles).
        if (!roleOptions.includes(role)) errors.push('Invalid role');
        if (errors.length) {
          dispatch(showSnackbar({ message: errors.join('; '), severity: 'warning' }));
          return;
        }
        const payload = {
          username,
          email,
          fullname,
          department: form.department || '',
          designation: (form.designation || '').trim() || null,
          role,
          sapusercode: form.sapusercode || '',
          docusign_user_api_id: (form.docusign_user_api_id || '').trim() || null,
          password,
        } as any;
        await apiService.register(payload);
        dispatch(showSnackbar({ message: 'User created successfully', severity: 'success' }));
      } else if (editState.mode === 'edit') {
        const id = form.id! as number;
        const emailTrim = (form.email || '').trim();
        const emailOk = /.+@.+\..+/.test(emailTrim);
        if (!emailTrim) {
          dispatch(showSnackbar({ message: 'Email is required', severity: 'warning' }));
          return;
        }
        if (!emailOk) {
          dispatch(showSnackbar({ message: 'Invalid email address', severity: 'warning' }));
          return;
        }
        const payload = {
          email: emailTrim,
          fullname: form.fullname,
          department: form.department,
          designation: (form.designation || '').trim() || null,
          role: form.role,
          isactive: form.isactive,
          sapusercode: form.sapusercode,
          docusign_user_api_id: (form.docusign_user_api_id || '').trim() || null,
        };
        await apiService.updateUser(id, payload);
        await apiService.setUserAdditionalRoles(id, additionalRoles.filter((r) => r !== form.role));
        dispatch(showSnackbar({ message: 'User updated successfully', severity: 'success' }));
      } else if (editState.mode === 'resetPassword') {
        const id = form.id! as number;
        const newPassword = form.password || '';
        if (!newPassword || newPassword.length < 8) {
          dispatch(showSnackbar({ message: 'Password must be at least 8 characters', severity: 'warning' }));
          return;
        }
        await apiService.resetUserPassword(id, newPassword);
        dispatch(showSnackbar({ message: 'Password reset. The user will choose a new password at the next sign-in.', severity: 'success' }));
      }
      closeDialog();
      fetchUsers();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Operation failed';
      const details = Array.isArray(err?.response?.data?.errors) ? (err.response.data.errors as any[]).map((e: any) => e?.message || '').filter(Boolean).join('; ') : '';
      dispatch(showSnackbar({ message: details ? `${msg}: ${details}` : msg, severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      dispatch(setLoading(true));
      const selectedCompanyId = localStorage.getItem('selectedCompanyId') || '';
      const baseURL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${baseURL}/user-management/users/download-template?companyId=${selectedCompanyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bulk_user_upload_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      dispatch(showSnackbar({ message: 'Template downloaded successfully', severity: 'success' }));
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Failed to download template', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleBulkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      dispatch(setLoading(true));
      const formData = new FormData();
      formData.append('file', file);

      const baseURL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${baseURL}/user-management/users/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Upload failed');
      }

      const data = result.data || {};
      const created = data.created || 0;
      const failed = data.failed || 0;
      const results = data.results || [];

      let message = `Bulk upload completed. Created: ${created}, Failed: ${failed}`;
      if (failed > 0) {
        const errors = results.filter((r: any) => r.status === 'failed').map((r: any) => `Row ${r.rowNum}: ${r.error}`).slice(0, 5).join('; ');
        message += `. Errors: ${errors}`;
        dispatch(showSnackbar({ message, severity: 'warning' }));
      } else {
        dispatch(showSnackbar({ message, severity: 'success' }));
      }

      fetchUsers();
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Bulk upload failed', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportUsers = async () => {
    try {
      dispatch(setLoading(true));
      const baseURL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${baseURL}/user-management/users/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export users');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `users_export_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      dispatch(showSnackbar({ message: 'Users exported successfully', severity: 'success' }));
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.message || 'Failed to export users', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const reviewUserCount = useMemo(() => users.filter((u) => (u.accessreviewcount || 0) > 0).length, [users]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return users.filter(u =>
      (!query ||
        u.username?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.fullname?.toLowerCase().includes(query) ||
        u.department?.toLowerCase().includes(query) ||
        u.role?.toLowerCase().includes(query)) &&
      (companyFilter === '' || u.role === 'Admin' || (u.companyids || []).includes(companyFilter)) &&
      (!reviewOnly || (u.accessreviewcount || 0) > 0)
    );
  }, [users, searchQuery, companyFilter, reviewOnly]);

  useEffect(() => { setPage(0); }, [searchQuery, companyFilter, reviewOnly]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  return (
    <Box
      component="section"
      aria-labelledby="user-management-heading"
      sx={{ p: { xs: 1.5, sm: 2 } }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={{ xs: 1.5, md: 0 }}
        mb={1.5}
      >
        <Typography
          component="h1"
          id="user-management-heading"
          variant="h6"
          sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}
        >
          User Management
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportUsers}>
            Export to Excel
          </Button>
          <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleDownloadTemplate}>
            Download Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
          >
            Bulk Upload
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleBulkUpload}
            />
          </Button>
          <Button variant="contained" onClick={openCreate}>Add User</Button>
        </Stack>
      </Stack>

      {reviewUserCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReviewOnly((v) => !v)}>
              {reviewOnly ? 'Show everyone' : 'Show users to review'}
            </Button>
          }
        >
          Access is now set per company. Each old project and cost-center grant was copied into every company of the user
          where the code exists, so nobody lost access on the day. {reviewUserCount} user{reviewUserCount === 1 ? ' has' : 's have'} grants
          to review: a code that now applies in two or more of their companies, a company given to a user who had none, or an
          old grant found in none of their companies.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems="center" flexWrap="wrap" mb={1.5}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 0.5 }} />
          }}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="role-filter-label">Role</InputLabel>
          <Select
            labelId="role-filter-label"
            label="Role"
            value={filters.role || ''}
            onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value || undefined }))}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {roleOptions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="company-filter-label">Company</InputLabel>
          <Select
            labelId="company-filter-label"
            label="Company"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">
              <em>All companies</em>
            </MenuItem>
            {availableCompanies.map((c) => (
              <MenuItem key={c.companyid} value={c.companyid}>
                {c.companyname}{c.databasename ? ` (${c.databasename})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={
            <Switch
              checked={!!filters.active}
              onChange={(e) => setFilters((f) => ({ ...f, active: e.target.checked ? true : undefined }))}
            />
          }
          label="Active Only"
        />
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={<Switch checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />}
          label="Access to review"
        />
        <Button variant="outlined" size="small" onClick={() => { setFilters({}); setSearchQuery(''); setCompanyFilter(''); setReviewOnly(false); }}>Clear All</Button>
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Companies</TableCell>
                <TableCell>Access per company</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.fullname}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                      {u.username}{u.email ? ` · ${u.email}` : ''}{u.department ? ` · ${u.department}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{u.role === 'Admin' || u.allcompanies ? 'All companies' : (u.companyids || []).length || 'None'}</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    {(u.role === 'Viewer' || u.role === 'ProcurementOfficer') && (
                      <Typography variant="body2" color="text.secondary">{u.role}: every PR of their companies</Typography>
                    )}
                    {u.role === 'Admin' ? (
                      <Typography variant="body2" color="text.secondary">Admin: everything</Typography>
                    ) : (u.accesssummary || []).length ? (
                      (u.accesssummary || []).map((s) => (
                        <Typography key={s.companyid} variant="body2" sx={{ lineHeight: 1.5 }}>
                          <strong>{s.companyname}</strong> · {describeAccess(s)}
                        </Typography>
                      ))
                    ) : u.role === 'Viewer' || u.role === 'ProcurementOfficer' ? null : (
                      <Typography variant="body2" color="text.secondary">Own documents and approvals only</Typography>
                    )}
                    {(u.accessreviewcount || 0) > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="To review"
                        onClick={() => openAccessDialog(u)}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={u.isactive ? 'Active' : 'Inactive'} color={u.isactive ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => openAccessDialog(u)}>Access</Button>
                    <IconButton
                      size="small"
                      aria-label="More actions"
                      onClick={(e) => { setRowMenuAnchorEl(e.currentTarget); setRowMenuUser(u); }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && paginatedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Menu
        anchorEl={rowMenuAnchorEl}
        open={rowMenuOpen}
        onClose={() => setRowMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => { if (rowMenuUser) openEdit(rowMenuUser); setRowMenuAnchorEl(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => { if (rowMenuUser) openCompanyDialog(rowMenuUser); setRowMenuAnchorEl(null); }}>Manage Companies</MenuItem>
        <MenuItem onClick={() => { if (rowMenuUser) openAccessDialog(rowMenuUser); setRowMenuAnchorEl(null); }}>Access (projects, divisions, departments)</MenuItem>
        <MenuItem onClick={() => { if (rowMenuUser) openModuleDialog(rowMenuUser); setRowMenuAnchorEl(null); }}>Module Access</MenuItem>
        <MenuItem onClick={() => { if (rowMenuUser) openResetPassword(rowMenuUser); setRowMenuAnchorEl(null); }}>Reset Password</MenuItem>
        <MenuItem
          onClick={async () => {
            if (!rowMenuUser) return;
            const confirmed = window.confirm(`Delete user ${rowMenuUser.username}?`);
            if (!confirmed) { setRowMenuAnchorEl(null); return; }
            try {
              dispatch(setLoading(true));
              await apiService.deleteUser(rowMenuUser.id);
              dispatch(showSnackbar({ message: 'User deleted successfully', severity: 'success' }));
              fetchUsers();
            } catch (err: any) {
              const msg = err?.response?.data?.message || err?.message || 'Failed to delete user';
              dispatch(showSnackbar({ message: msg, severity: 'error' }));
            } finally {
              dispatch(setLoading(false));
            }
            setRowMenuAnchorEl(null);
          }}
        >
          Delete
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!rowMenuUser) return;
            try {
              dispatch(setLoading(true));
              if (rowMenuUser.isactive) {
                await apiService.deactivateUser(rowMenuUser.id);
                dispatch(showSnackbar({ message: 'User deactivated successfully', severity: 'success' }));
              } else {
                await apiService.updateUser(rowMenuUser.id, { isactive: true });
                dispatch(showSnackbar({ message: 'User activated successfully', severity: 'success' }));
              }
              fetchUsers();
            } catch (err: any) {
              const msg = err?.response?.data?.message || err?.message || 'Failed to update status';
              dispatch(showSnackbar({ message: msg, severity: 'error' }));
            } finally {
              dispatch(setLoading(false));
            }
            setRowMenuAnchorEl(null);
          }}
        >
          {rowMenuUser?.isactive ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>

      <DraggableDialog open={editState.open} onClose={closeDialog} maxWidth="sm" fullWidth title={
        editState.mode === 'create' ? 'Add New User' : editState.mode === 'edit' ? 'Edit User' : `Reset Password: ${editState.user?.username || ''}`
      } actions={
        <>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editState.mode === 'resetPassword' ? 'Reset' : 'Save'}
          </Button>
        </>
      }>
        {editState.mode !== 'resetPassword' ? (
          <Stack mt={1} spacing={2}>
            <TextField
              label="Username"
              value={form.username || ''}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              required
              disabled={editState.mode === 'edit'}
            />
            <TextField label="Full Name" value={form.fullname || ''} onChange={(e) => setForm((f) => ({ ...f, fullname: e.target.value }))} required />
            <TextField label="Email" value={form.email || ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
            <TextField label="Department" value={form.department || ''} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} />
            <TextField
              label="Designation"
              value={form.designation || ''}
              onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
              placeholder="e.g. Procurement Officer, Project Manager"
              helperText="Job title — printed on the Payment Certificate next to the Initiator's name. Leave blank to fall back to Department / Role."
            />
            <FormControl>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                label="Role"
                value={form.role || ''}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {roleOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editState.mode === 'edit' && (
              <Autocomplete
                multiple
                options={roleOptions.filter((r) => r !== form.role)}
                value={additionalRoles.filter((r) => r !== form.role)}
                onChange={(_e, v) => setAdditionalRoles(v)}
                renderTags={(value, getTagProps) => value.map((r, i) => <Chip {...getTagProps({ index: i })} key={r} size="small" label={r} />)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Additional roles"
                    helperText="Held on top of the main role, e.g. ItemFinanceReview or ProcurementManager for the item request approvals."
                  />
                )}
              />
            )}
            <TextField label="SAP User Code" value={form.sapusercode || ''} onChange={(e) => setForm((f) => ({ ...f, sapusercode: e.target.value }))} />
            <TextField
              label="DocuSign User API ID"
              value={form.docusign_user_api_id || ''}
              onChange={(e) => setForm((f) => ({ ...f, docusign_user_api_id: e.target.value }))}
              placeholder="UUID e.g. 12345678-1234-1234-1234-1234567890ab"
              helperText="Optional. From DocuSign Admin → Users → API User ID. When set, envelopes are sent on behalf of this user (Tier B impersonation). Leave blank to use the integration's service account."
            />
            {editState.mode === 'create' && (
              <TextField label="Password (default Welcome@123 if blank)" type="password" value={form.password || ''} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} helperText="Min 8 chars with uppercase, lowercase, number, special char" />
            )}
            {editState.mode === 'edit' && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography>Active</Typography>
                <Switch checked={!!form.isactive} onChange={(e) => setForm((f) => ({ ...f, isactive: e.target.checked }))} />
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack mt={1} spacing={2}>
            <TextField
              label="New Password"
              type="password"
              value={form.password || ''}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              helperText="Minimum 8 characters. The user signs in with it once, then chooses their own password."
              required
            />
          </Stack>
        )}
      </DraggableDialog>

      {/* Company linking dialog */}
      <DraggableDialog open={companyDialogOpen} onClose={() => { setCompanyDialogOpen(false); fetchUsers(); }} maxWidth="sm" fullWidth title={`Manage Companies for ${companyDialogUser?.fullname || ''}`} actions={
        <>
          <Button
            disabled={!companyDialogUser}
            onClick={() => { const u = companyDialogUser; setCompanyDialogOpen(false); if (u) openAccessDialog(u); }}
          >
            Access per company
          </Button>
          <Button onClick={() => { setCompanyDialogOpen(false); fetchUsers(); }}>Close</Button>
        </>
      }>
          <Stack spacing={2} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={userAllCompanies}
                  disabled={!companyDialogUser}
                  onChange={async (e) => {
                    if (!companyDialogUser) return;
                    const all = e.target.checked;
                    try {
                      dispatch(setLoading(true));
                      await apiService.setUserAllCompanies(companyDialogUser.id, all);
                      setUserAllCompanies(all);
                      dispatch(showSnackbar({ message: all ? 'Access to all companies granted' : 'All-companies access removed', severity: 'success' }));
                    } catch (err: any) {
                      dispatch(showSnackbar({ message: err?.message || 'Failed to change all-companies access', severity: 'error' }));
                    } finally {
                      dispatch(setLoading(false));
                    }
                  }}
                />
              }
              label="All companies, including companies added later"
            />
            {userAllCompanies && (
              <Typography variant="body2" color="text.secondary">
                This user can use every active company. The companies linked below still set the primary company. A company
                added later starts with no project, division or department granted: set them in Access per company.
              </Typography>
            )}
            <FormControl fullWidth>
              <InputLabel id="company-select-label">Company</InputLabel>
              <Select
                labelId="company-select-label"
                label="Company"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
              >
                <MenuItem value="">
                  <em>Select company</em>
                </MenuItem>
                {availableCompanies.map((c) => (
                  <MenuItem key={c.companyid} value={c.companyid}>
                    {c.companycode} - {c.companyname}{c.databasename ? ` (${c.databasename})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography>Primary</Typography>
              <Switch checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
            </Stack>
            <Button
              variant="contained"
              disabled={!selectedCompanyId || !companyDialogUser}
              onClick={async () => {
                if (!companyDialogUser || !selectedCompanyId) return;
                try {
                  dispatch(setLoading(true));
                  await apiService.addUserCompany(companyDialogUser.id, { companyid: Number(selectedCompanyId), isprimary: isPrimary });
                  dispatch(showSnackbar({ message: 'Company linked to user', severity: 'success' }));
                  await loadUserCompanies(companyDialogUser.id);
                  setSelectedCompanyId('');
                  setIsPrimary(false);
                } catch (err: any) {
                  dispatch(showSnackbar({ message: err?.message || 'Failed to link company', severity: 'error' }));
                } finally {
                  dispatch(setLoading(false));
                }
              }}
            >
              Link Company
            </Button>
            <Typography variant="subtitle1">Linked Companies</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Database</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userCompanies.map((uc) => (
                  <TableRow key={`${uc.companyid}`}>
                    <TableCell>{uc.companycode}</TableCell>
                    <TableCell>{uc.companyname}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{uc.databasename || '-'}</TableCell>
                    <TableCell>{uc.isprimary ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={async () => {
                          if (!companyDialogUser) return;
                          if (!window.confirm(`Remove ${uc.companyname} from ${companyDialogUser.fullname || companyDialogUser.username}? `
                            + 'The projects, divisions and departments granted in that company are removed too.')) return;
                          try {
                            dispatch(setLoading(true));
                            await apiService.removeUserCompany(companyDialogUser.id, uc.companyid);
                            dispatch(showSnackbar({ message: 'Company unlinked', severity: 'success' }));
                            await loadUserCompanies(companyDialogUser.id);
                          } catch (err: any) {
                            dispatch(showSnackbar({ message: err?.message || 'Failed to unlink company', severity: 'error' }));
                          } finally {
                            dispatch(setLoading(false));
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {userCompanies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">No companies linked</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Stack>
      </DraggableDialog>

      <UserAccessDialog
        open={!!accessDialogUser}
        user={accessDialogUser}
        onClose={closeAccessDialog}
        onChanged={() => { accessChanged.current = true; }}
        onManageCompanies={() => {
          const u = accessDialogUser;
          closeAccessDialog();
          if (u) openCompanyDialog(u);
        }}
      />

      {/* Module Access dialog */}
      <DraggableDialog open={moduleDialogOpen} onClose={() => setModuleDialogOpen(false)} maxWidth="md" fullWidth title={`Module Access for ${moduleDialogUser?.fullname || ''}`} actions={<Button onClick={() => setModuleDialogOpen(false)}>Close</Button>}>
          <Stack spacing={1.5} mt={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                All menu items, including Settings, are controlled here.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {hiddenAssignedModules.length > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={`${hiddenAssignedModules.length} old key${hiddenAssignedModules.length === 1 ? '' : 's'} ignored`}
                    title={hiddenAssignedModules.join(', ')}
                  />
                )}
                <Chip size="small" label={`${visibleAssignedCount}/${allModules.length} assigned`} />
              </Stack>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5,
                maxHeight: '65vh',
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {moduleGroups.map(([category, modules]) => (
                <Paper key={category} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {category}
                      </Typography>
                      <Chip size="small" label={`${modules.filter((m) => userModuleSet.has(m.key)).length}/${modules.length}`} />
                    </Stack>
                    {modules.map((m) => {
                      const checked = userModuleSet.has(m.key);
                      return (
                        <Stack key={m.key} direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ minHeight: 34 }}>
                          <Typography variant="body2" sx={{ pr: 1, lineHeight: 1.2 }}>
                            {m.label}
                          </Typography>
                          <Switch
                            size="small"
                            checked={checked}
                            onChange={async (e) => {
                              if (!moduleDialogUser) return;
                              try {
                                dispatch(setLoading(true));
                                if (e.target.checked) {
                                  await apiService.addUserModule(moduleDialogUser.id, m.key);
                                  setUserModules((mods) => Array.from(new Set([...mods, m.key])));
                                  dispatch(showSnackbar({ message: `${m.label} granted`, severity: 'success' }));
                                } else {
                                  await apiService.removeUserModule(moduleDialogUser.id, m.key);
                                  setUserModules((mods) => mods.filter((mk) => mk !== m.key));
                                  dispatch(showSnackbar({ message: `${m.label} revoked`, severity: 'success' }));
                                }
                              } catch (err: any) {
                                dispatch(showSnackbar({ message: err?.message || 'Failed to update module access', severity: 'error' }));
                              } finally {
                                dispatch(setLoading(false));
                              }
                            }}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Stack>
      </DraggableDialog>
    </Box>
  );
}
